import {Exclude, Expose, instanceToPlain, plainToInstance} from "class-transformer";
import * as t from "io-ts";
import {validateJSON, validateJSONNow} from "../web/validation";
import {Entity} from "./entity";
import {adjustFromTimeZone, adjustFromTimeZoneSafely, format} from "../date/date";
import {Exportable} from "./exportable";

export const SiteSiteAssocValidator = t.intersection([
  t.type({
    id: t.string,
    sourceSiteId: t.string,
    destSiteId: t.string,
    effectiveDttm: t.number,
  }),
  t.partial({
    regionIds: t.array(t.string),
    mileage: t.number,
    endDttm: t.number,
    sourceSiteName: t.string,
    destSiteName: t.string,
    createDttm: t.number,
    lastModifiedDttm: t.number,
  }),
]);

export const SiteSiteAssocArrayValidator = t.array(SiteSiteAssocValidator);

export type SiteSiteAssoc = t.TypeOf<typeof SiteSiteAssocValidator>;
export type SiteSiteAssocArray = t.TypeOf<typeof SiteSiteAssocArrayValidator>;

@Exclude()
export class SiteSiteAssocEntity implements Entity, Exportable {
  @Expose()
  id!: string;

  @Expose()
  sourceSiteId!: string;

  @Expose()
  sourceSiteName?: string

  @Expose()
  destSiteId!: string;

  @Expose()
  destSiteName?: string

  @Expose()
  regionIds?: string[];

  @Expose()
  mileage?: number;

  @Expose()
  effectiveDttm!: number;

  @Expose()
  endDttm?: number;

  @Expose()
  createDttm?: number;

  @Expose()
  lastModifiedDttm?: number;

  readonly entityType = 'SITESITEASSOC';

  get displayName(): Readonly<string> {
    return this.id;
  }

  /**
   * Constructor for SiteSiteAssocEntity.
   * @hideconstructor
   */
  constructor() {
    // Empty constructor for 'class-transformer'
  }

  static from(value: SiteSiteAssoc): SiteSiteAssocEntity {
    return plainToInstance(SiteSiteAssocEntity, value, {
      excludeExtraneousValues: true,
    });
  }

  loadTypeValue(): Promise<SiteSiteAssoc> {
    const object = instanceToPlain(this, {
      excludeExtraneousValues: true,
    });
    return validateJSON(SiteSiteAssocValidator, object);
  }

  asTypeValue(): SiteSiteAssoc {
    const object = instanceToPlain(this, {
      excludeExtraneousValues: true,
    });
    return validateJSONNow(SiteSiteAssocValidator, object);
  }

  isActive = (dttm: number = new Date().getTime()): boolean => {
    const adjustedStartDttm = adjustFromTimeZoneSafely(this.effectiveDttm)?.getTime()
    const adjustedEndDttm = adjustFromTimeZoneSafely(this.endDttm)?.getTime()

    const meetsStartThresh = adjustedStartDttm == null || adjustedStartDttm <= dttm
    const meetsEndThresh = adjustedEndDttm == null || adjustedEndDttm > dttm

    return meetsStartThresh && meetsEndThresh
  }

  readonly CSV_HEADERS: string[] = [
    'Source Site',
    'Dest Site',
    'Mileage',
    'Effective',
    'End',
  ];

  asCsv(): string[] {
    return [
      this.sourceSiteName ?? '',
      this.destSiteName ?? '',
      this.mileage?.toString() ?? '',
      format(adjustFromTimeZone(this.effectiveDttm), 'yyyy-MM-dd HH:mm:ss'),
      this.endDttm ? format(adjustFromTimeZone(this.endDttm), 'yyyy-MM-dd HH:mm:ss') : '',
    ];
  }
}
